/**
 * Bấm một tin là tới đúng chỗ — DÙNG CHUNG cho chuông và trang "Thông báo của
 * tôi". Hai chỗ chép riêng là hai luật điều hướng trôi khỏi nhau.
 */

import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMarkRead, type StaffNotification } from "./api";

/**
 * Đường dẫn trong app của một tin. Dòng chuông chép từ Telegram mang địa chỉ
 * ĐẦY ĐỦ (`https://erp.gaonau.com/customer-voice` — Telegram cần thế); đưa
 * nguyên chuỗi đó cho bộ định tuyến thì nó coi là đường tương đối và nhảy tới
 * `/orders/https:/erp…` (soát thông báo 17/10/2026). Chỉ giữ phần đường + hỏi.
 */
export function appPath(link: string | null | undefined): string {
  const raw = (link ?? "").trim();
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const u = new URL(raw);
    return u.pathname + u.search;
  } catch {
    return "";
  }
}

/**
 * Địa chỉ đầy đủ khi tin trỏ sang Cổng dịch vụ (chuông yêu cầu tư vấn mở Sổ
 * yêu cầu của người trực — trang đó không có trong menu nào). CHỈ đúng gốc Cổng
 * mới mở ra ngoài; mọi địa chỉ đầy đủ khác vẫn bị cắt về đường trong app như cũ.
 */
export function portalTarget(
  link: string | null | undefined,
  portalBase: string = import.meta.env.VITE_CUSTOMER_PORTAL_URL ?? "",
): string | null {
  const raw = (link ?? "").trim();
  if (!raw || !portalBase || !/^https?:\/\//i.test(raw)) return null;
  try {
    const portal = new URL(portalBase);
    const target = new URL(raw);
    return target.origin === portal.origin ? target.href : null;
  } catch {
    return null;
  }
}

/** Nơi bấm tin dẫn tới; `null` = tin không dẫn đi đâu (chỉ để đọc). */
export function notificationTarget(
  n: Pick<StaffNotification, "orderId" | "link">,
): { to: string; params?: { orderId: string }; search?: Record<string, string> } | null {
  if (n.orderId) return { to: "/orders/$orderId", params: { orderId: n.orderId } };
  // Tin KHÔNG gắn đơn (lời mời nhận suất, báo nghỉ, đơn in…) đi theo đường
  // dẫn máy chủ ghi kèm. Máy chủ ghi chuỗi thô kiểu "/ekip-shifts?tab=absence";
  // bộ định tuyến khớp theo ĐƯỜNG, phần hỏi phải tách sang `search` — nhét cả
  // chuỗi vào `to` là không đường nào khớp và bấm vào chẳng đi đâu.
  const link = appPath(n.link);
  if (!link) return null;
  const [to, query] = link.split("?");
  if (!to) return null;
  return query ? { to, search: Object.fromEntries(new URLSearchParams(query)) } : { to };
}

/**
 * Trả hàm "mở tin": đánh dấu đã đọc rồi đi tới đơn / đường dẫn của tin.
 *
 * Đánh dấu đọc theo ĐƠN chứ không theo dòng khi tin gắn với một đơn: một đơn
 * đẻ ra nhiều tin (tiền về, kế toán duyệt, kế toán từ chối), đọc một dòng mà
 * dấu trên đơn vẫn sáng là người dùng bấm mãi không tắt.
 */
export function useOpenNotification(): (n: StaffNotification) => Promise<void> {
  const navigate = useNavigate();
  const { mutateAsync } = useMarkRead();
  return useCallback(
    async (n: StaffNotification) => {
      try {
        await mutateAsync(n.orderId ? { orderId: n.orderId } : { ids: [n.id] });
      } catch {
        // Đánh dấu hụt không được chặn đường đi: người dùng bấm là để XEM,
        // không phải để dọn hộp thư.
      }
      // Tin trỏ sang Cổng mở tab mới: Cổng là trang khác, giữ nguyên ERP đang mở.
      const portal = n.orderId ? null : portalTarget(n.link);
      if (portal) {
        window.open(portal, "_blank", "noopener,noreferrer");
        return;
      }
      const target = notificationTarget(n);
      if (!target) return;
      void navigate(target as never);
    },
    [mutateAsync, navigate],
  );
}
